import { FormServices } from "@/service/FormServices";
import { defaultColorsArray } from "@/util/constants";

export type StoreColorSwatch = {
  value: string;
  value8?: string;
  alpha?: number;
  label?: string;
  labelValue?: string;
};

/**
 * @description Ensures a hex-like string is prefixed with '#'.
 *
 * @param val - Input value that may or may not include '#'
 * @returns Normalized string with '#' prefix, or empty string for falsy input
 */
export const ensureHash = (val?: unknown): string => {
  if (!val) {
    return "";
  }
  const str = String(val).trim();
  if (!str) {
    return "";
  }
  return str.startsWith("#") ? str : `#${str}`;
};

/**
 * @description Normalize an arbitrary swatch payload into store-compatible `StoreColorSwatch`.
 *
 * @param swatch - Raw swatch object emitted by HLColorPicker or returned by API.
 * @returns Normalized swatch or null when input is invalid.
 */
export const normalizeDefaultSwatch = (swatch: unknown): StoreColorSwatch | null => {
  if (!swatch || typeof swatch !== "object") {
    return null;
  }
  const s = swatch as Record<string, unknown>;

  const value8 = ensureHash(s?.value8 ?? s?.value);
  const label = String(s?.label ?? s?.labelValue ?? s?.name ?? value8 ?? "").trim();
  if (!value8) {
    return null;
  }

  return {
    label,
    labelValue: String(s?.labelValue ?? label),
    value: value8,
    value8,
    alpha: Number.isFinite(s?.alpha as number) ? Number(s?.alpha) : 100,
  };
};

/**
 * @description Convert any array of raw colors into normalized store swatches,
 * falling back to `defaultColorsArray` when empty/invalid.
 *
 * @param colors - Raw colors array
 * @returns Normalized palette
 */
export const normalizeDefaultColors = (colors?: unknown): Array<StoreColorSwatch> => {
  const normalized = Array.isArray(colors)
    ? colors
        .map((swatch) => normalizeDefaultSwatch(swatch))
        .filter((s): s is StoreColorSwatch => Boolean(s))
    : [];

  return normalized.length ? normalized : [...defaultColorsArray];
};

/**
 * @description Determines whether a swatch CRUD payload should affect default colors.
 *
 * @param payload - Event payload from HLColorPicker
 * @returns True if payload is for the default section (or has no sectionKey).
 */
export const isDefaultColorsSectionPayload = (payload?: unknown): boolean => {
  if (!payload || typeof payload !== "object") {
    return true;
  }
  const sectionKey = (payload as Record<string, unknown>)?.sectionKey;
  return !sectionKey || sectionKey === "default";
};

/**
 * @description Build API payload for saving default colors.
 *
 * @param palette - Normalized palette to save
 * @returns API payload format
 */
export const buildDefaultColorsSavePayload = (
  palette: Array<StoreColorSwatch>
): Array<{ name?: string; value: string }> => {
  return (palette || []).map((c) => ({
    name: c.label || c.labelValue || c.value,
    value: c.value,
  }));
};

/**
 * @description Fetch and normalize default colors from the server.
 *
 * @param locationId - Location identifier
 * @returns Normalized palette
 */
export const fetchDefaultColors = async (locationId: string): Promise<Array<StoreColorSwatch>> => {
  const { data } = await FormServices.getDefaultColors(locationId);
  return normalizeDefaultColors((data as { colors?: unknown[] } | undefined)?.colors);
};

/**
 * @description Save default colors to the server and return the persisted palette.
 *
 * @param locationId - Location identifier
 * @param palette - Palette to persist
 * @returns Normalized palette returned by API (or the input palette normalized)
 */
export const saveDefaultColors = async (
  locationId: string,
  palette: Array<StoreColorSwatch>
): Promise<Array<StoreColorSwatch>> => {
  const payload = buildDefaultColorsSavePayload(palette);
  const { data } = await FormServices.saveDefaultColors(locationId, payload);
  return normalizeDefaultColors((data as { colors?: unknown[] } | undefined)?.colors ?? palette);
};

/**
 * @description Upsert a color into the default palette based on value/label match.
 *
 * @param current - Current palette
 * @param payload - Swatch payload (event or object)
 * @returns Next palette or null if no-op
 */
export const getNextDefaultColorsOnAdd = (
  current: Array<StoreColorSwatch> | undefined,
  payload: any
): Array<StoreColorSwatch> | null => {
  if (!isDefaultColorsSectionPayload(payload)) {
    return null;
  }
  const swatch = normalizeDefaultSwatch(payload?.swatch ?? payload);
  if (!swatch?.value) {
    return null;
  }

  const next = Array.isArray(current) ? [...current] : [];
  const existingIndex = next.findIndex((c) => c.value === swatch.value || c.label === swatch.label);
  if (existingIndex >= 0) {
    next[existingIndex] = swatch;
  } else {
    next.push(swatch);
  }
  return next;
};

/**
 * @description Update a specific color in the default palette.
 *
 * @param current - Current palette
 * @param payload - Swatch payload (event or object)
 * @returns Next palette or null if no-op
 */
export const getNextDefaultColorsOnEdit = (
  current: Array<StoreColorSwatch> | undefined,
  payload: any
): Array<StoreColorSwatch> | null => {
  if (!isDefaultColorsSectionPayload(payload)) {
    return null;
  }
  const swatch = normalizeDefaultSwatch(payload?.swatch ?? payload);
  if (!swatch?.value) {
    return null;
  }

  const next = Array.isArray(current) ? [...current] : [];
  const index =
    typeof payload?.index === "number"
      ? payload.index
      : next.findIndex((c) => c.value === swatch.value || c.label === swatch.label);
  if (index < 0) {
    return null;
  }
  next[index] = swatch;
  return next;
};

/**
 * @description Extract the swatch value for delete API calls.
 *
 * @param payload - Swatch payload (event or object)
 * @returns Hex value string (with '#') or empty string
 */
export const getDefaultColorDeleteValue = (payload: any): string => {
  const raw = payload?.swatch?.value ?? payload?.value;
  if (!raw) {
    return "";
  }
  if (typeof raw === "string") {
    return ensureHash(raw);
  }
  if (typeof raw === "object") {
    const obj = raw as Record<string, unknown>;
    return ensureHash(obj?.value ?? obj?.value8);
  }
  return "";
};

/**
 * @description Delete a default color from the server and return the updated palette.
 *
 * @param locationId - Location identifier
 * @param value - Swatch value to delete
 * @returns Normalized palette
 */
export const deleteDefaultColorFromServer = async (
  locationId: string,
  value: string
): Promise<Array<StoreColorSwatch>> => {
  const { data } = await FormServices.deleteDefaultColor(locationId, { value });
  return normalizeDefaultColors((data as { colors?: unknown[] } | undefined)?.colors);
};
